import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useProjects } from '../../contexts/ProjectContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiChevronDown, FiChevronRight, FiCheck, FiSave } = FiIcons;

const StoryStructureOutline = ({ project }) => {
  const { updateProject } = useProjects();
  const [expandedSections, setExpandedSections] = useState({});
  const [formData, setFormData] = useState({ storyStructure: {} });
  const [isLoaded, setIsLoaded] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // 'saving','saved',null
  const [debounceTimeout, setDebounceTimeout] = useState(null);
  const [manualSaveNeeded, setManualSaveNeeded] = useState(false); // Track if manual save is needed
  const initializedRef = useRef(false);

  // Initialize form data and expanded state ONLY once when project first loads
  useEffect(() => {
    if (project && project.content && !initializedRef.current) {
      console.log('Initializing story structure from project content (first time only)');
      const projectStructure = project.content.story_structure || {};
      setFormData({ storyStructure: projectStructure });

      // Initialize expanded state for movements - only expand first movement by default
      const movementsExpanded = {};
      const movements = [
        'openingScene', 'hookingMoment', 'firstPlotPoint', 'firstPinchPoint', 'midpointShift',
        'secondPinchPoint', 'secondPlotPoint', 'finalResolution', 'worldBackToNormal'
      ];
      movements.forEach((key, index) => {
        movementsExpanded[key] = index === 0; // Only expand first movement by default
      });
      setExpandedSections(movementsExpanded);
      setIsLoaded(true);
      initializedRef.current = true;
      setManualSaveNeeded(false);
    }
  }, [project?.id]); // Only depend on project ID

  // Separate effect to update form data when project content changes (but preserve expanded state)
  useEffect(() => {
    if (project && project.content && initializedRef.current) {
      console.log('Updating story structure data (preserving expanded state)');
      const projectStructure = project.content.story_structure || {};
      setFormData({ storyStructure: projectStructure });
      // Note: We don't touch expandedSections here
      setManualSaveNeeded(false);
    }
  }, [project?.content?.story_structure]);

  // Calculate progress based on filled fields
  const calculateProgress = (updatedStructure) => {
    // Get all form data from the parent component
    const parentContent = project.content || {};

    // Define all fields to check for progress calculation
    const allFields = [
      // Story Concept fields
      'premise', 'theme', 'faith_element', 'story_beginning', 'story_middle', 'story_end', 'reader_transformation',
      // Character fields
      'protagonist', 'protagonist_goals', 'protagonist_flaw', 'antagonist', 'antagonist_motivations', 'supporting_characters',
      // Story World fields
      'setting', 'time_period', 'world_rules', 'conflict', 'stakes', 'spiritual_elements'
    ];

    // Count filled fields from parent content
    let filledFields = 0;
    allFields.forEach(field => {
      if (parentContent[field] && parentContent[field].toString().trim()) {
        filledFields++;
      }
    });

    // Add story structure progress
    const structureFields = [
      'openingScene', 'hookingMoment', 'firstPlotPoint', 'firstPinchPoint', 'midpointShift',
      'secondPinchPoint', 'secondPlotPoint', 'finalResolution', 'worldBackToNormal'
    ];
    let structureFilledFields = 0;
    structureFields.forEach(field => {
      if (updatedStructure[field] && updatedStructure[field].trim()) {
        structureFilledFields++;
      }
    });

    // Total fields = base fields + structure fields
    const totalFields = allFields.length + structureFields.length;
    const totalFilledFields = filledFields + structureFilledFields;

    return Math.round((totalFilledFields / totalFields) * 100);
  };

  const handleChange = (movementId, value) => {
    // Update local state immediately
    const updatedStructure = { ...formData.storyStructure, [movementId]: value };
    setFormData(prev => ({ ...prev, storyStructure: updatedStructure }));
    
    // Mark as needing manual save
    setManualSaveNeeded(true);

    // Show saving indicator
    setSaveStatus('saving');

    // Clear any existing timeout
    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }

    // Set new timeout for saving with a 3-second delay
    const timeoutId = setTimeout(async () => {
      try {
        // Calculate progress
        const progress = calculateProgress(updatedStructure);

        // Update project with new structure and progress
        await updateProject(project.id, {
          storyStructure: updatedStructure,
          progress: progress
        });

        setSaveStatus('saved');
        setManualSaveNeeded(false);

        // Clear the "saved" status after 2 seconds
        setTimeout(() => {
          setSaveStatus(null);
        }, 2000);
      } catch (error) {
        console.error('Error saving structure:', error);
        setSaveStatus(null);
      }
    }, 3000); // 3-second delay

    setDebounceTimeout(timeoutId);
  };
  
  // Manual save function
  const handleManualSave = async () => {
    if (!manualSaveNeeded) return; // Don't save if no changes
    
    setSaveStatus('saving');
    
    try {
      const updatedStructure = formData.storyStructure;
      
      // Calculate progress
      const progress = calculateProgress(updatedStructure);
      
      // Update project with all data at once
      await updateProject(project.id, {
        storyStructure: updatedStructure,
        progress: progress
      });
      
      setSaveStatus('saved');
      setManualSaveNeeded(false);
      
      // Clear the "saved" status after 2 seconds
      setTimeout(() => {
        setSaveStatus(null);
      }, 2000);
    } catch (error) {
      console.error('Error saving structure:', error);
      setSaveStatus(null);
    }
  };

  const toggleSection = (sectionId) => {
    console.log('Toggling section:', sectionId, 'Current state:', expandedSections[sectionId]);
    setExpandedSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  const movements = [
    {
      id: 'openingScene',
      title: 'Movement 1: Opening Scene',
      description: 'Orient the reader in the world as the hero knows it. Create depth through emotions, opinions, and 5 senses of the character. Establish the "normal world" before change comes.',
      placeholder: 'Describe your opening scene that establishes the hero and creates reader connection. Show their ordinary world and what makes them relatable...'
    },
    {
      id: 'hookingMoment',
      title: 'Movement 2: Hooking Moment',
      description: 'Introduce the first moment of intrigue that disrupts the status quo. This is where you plant the story question that will drive the reader forward.',
      placeholder: 'What unexpected moment or event captures the reader\'s attention and signals that something is about to change?'
    },
    {
      id: 'firstPlotPoint',
      title: 'Movement 3: First Plot Point',
      description: 'The moment where the protagonist crosses the threshold into the main conflict. Something happens that forces a decision or change of direction.',
      placeholder: 'What event or decision forces your protagonist to engage with the main story problem?'
    },
    {
      id: 'firstPinchPoint',
      title: 'Movement 4: First Pinch Point',
      description: 'A moment where the antagonistic force shows its power or the stakes are raised. The challenge feels more real and dangerous.',
      placeholder: 'How does the antagonistic force (person, situation, or internal struggle) reveal its true threat?'
    },
    {
      id: 'midpointShift',
      title: 'Movement 5: Midpoint Shift',
      description: 'The moment where everything changes - new information is revealed, the protagonist shifts from reactive to proactive, or a major truth is uncovered.',
      placeholder: 'What major revelation, decision, or shift in perspective changes everything at the middle of your story?'
    },
    {
      id: 'secondPinchPoint',
      title: 'Movement 6: Second Pinch Point',
      description: 'The antagonistic force strikes again, harder than before. The protagonist faces serious setbacks that test their resolve.',
      placeholder: 'How does the antagonistic force push back against the protagonist\'s progress? What setbacks occur?'
    },
    {
      id: 'secondPlotPoint',
      title: 'Movement 7: Second Plot Point',
      description: 'The final piece of information or resource the protagonist needs to move into the final confrontation. The last moment of preparation before the climax.',
      placeholder: 'What final revelation, tool, or insight prepares your protagonist for the final confrontation?'
    },
    {
      id: 'finalResolution',
      title: 'Movement 8: Final Resolution',
      description: 'The climactic confrontation where the protagonist faces the antagonistic force and the main story question is answered.',
      placeholder: 'How does your protagonist confront and overcome (or fail to overcome) the central conflict?'
    },
    {
      id: 'worldBackToNormal',
      title: 'Movement 9: World Back to Normal',
      description: 'Show how the protagonist and their world have been transformed by the journey. Tie up loose ends and show the new status quo.',
      placeholder: 'How has your protagonist and their world changed? What does the new normal look like?'
    }
  ];

  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-secondary-600">Loading structure data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-secondary-800">StoryWise™ Framework</h2>
          <p className="text-secondary-600 mt-2">
            Structure your story using the proven 9-movement framework for compelling fiction.
          </p>
        </div>
        
        {/* Save Status and Manual Save Button */}
        <div className="flex items-center space-x-3">
          {saveStatus && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className={`flex items-center space-x-2 px-3 py-1 rounded-lg ${
                saveStatus === 'saving' ? 'bg-primary-50 text-primary-600' : 'bg-green-50 text-green-600'
              }`}
            >
              {saveStatus === 'saving' ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
                  <span className="text-sm">Saving...</span>
                </>
              ) : (
                <>
                  <SafeIcon icon={FiCheck} className="w-4 h-4" />
                  <span className="text-sm">Saved</span>
                </>
              )}
            </motion.div>
          )}
          
          {/* Manual save button */}
          {manualSaveNeeded && (
            <button 
              onClick={handleManualSave}
              className="flex items-center space-x-2 bg-primary-600 text-white px-3 py-2 rounded-lg hover:bg-primary-700 transition-colors"
            >
              <SafeIcon icon={FiSave} className="w-4 h-4" />
              <span>Save Changes</span>
            </button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="space-y-4">
          {movements.map((movement) => (
            <motion.div
              key={movement.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="border border-secondary-200 rounded-lg overflow-hidden"
            >
              <div
                className="flex items-center justify-between bg-gradient-to-r from-primary-50 to-spiritual-50 p-4 cursor-pointer"
                onClick={() => toggleSection(movement.id)}
              >
                <h3 className="font-semibold text-secondary-800">{movement.title}</h3>
                <SafeIcon
                  icon={expandedSections[movement.id] ? FiChevronDown : FiChevronRight}
                  className="w-5 h-5 text-secondary-600"
                />
              </div>
              {expandedSections[movement.id] && (
                <div className="p-4 space-y-4">
                  <p className="text-secondary-600 text-sm">{movement.description}</p>
                  <textarea
                    value={formData.storyStructure[movement.id] || ''}
                    onChange={(e) => handleChange(movement.id, e.target.value)}
                    rows={4}
                    className="w-full px-4 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition-all"
                    placeholder={movement.placeholder}
                  />
                </div>
              )}
            </motion.div>
          ))}
        </div>
        
        {/* Manual Save Button at Bottom */}
        {manualSaveNeeded && (
          <div className="flex justify-end mt-6">
            <button 
              onClick={handleManualSave}
              className="flex items-center space-x-2 bg-gradient-to-r from-primary-600 to-spiritual-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-primary-700 hover:to-spiritual-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              <SafeIcon icon={FiSave} className="w-5 h-5" />
              <span>Save All Changes</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default StoryStructureOutline;